import { ColorName, useTheme } from '@emotion/react';
import { first, last } from '@quromedical/utils';
import * as Graph from 'components/graph';
import { Margin } from 'components/graph';
import { usePlotDataContext } from 'components/graph';
import { Visible, Skeleton } from 'design-system/base';
import { FONT_FAMILY, FONT_SIZE } from 'design-system/theme';
import { dateTimeWithoutYearFormatter, dateWithoutYearFormatter, timeFormatter } from 'helpers';
import { useLayoutDimensions } from 'hooks/useLayoutDimensions';
import React from 'react';
import { View } from 'react-native';
import { Text } from 'react-native-svg';

import { ToggleCard, ToggleCardProps } from '../toggle-card';
import { GetClosest, useTooltipProps } from './useTooltipProps';

export interface TimelinePoint extends Graph.Datum {
  text: string;
}

type Formatter = (value: number) => string;

type TimeFormat = 'time' | 'date' | 'date-time';

const timeFormatters: Record<TimeFormat, Formatter> = {
  'date-time': dateTimeWithoutYearFormatter,
  time: timeFormatter,
  date: dateWithoutYearFormatter,
};

interface LineProps {
  points: TimelinePoint[];
  stops?: Graph.StopProps[];
  index: number;
  color?: ColorName;
  showScatter?: boolean;
  hideTimeline?: boolean;
  getClosest: GetClosest;
}

type LineData = Pick<LineProps, 'color' | 'points' | 'stops' | 'showScatter' | 'hideTimeline'>;

interface TimelinePointWithColor extends TimelinePoint {
  color: ColorName;
}

export interface StatusLineData {
  start: number;
  end: number;
  color: ColorName;
  text: string;
  width?: number;
}
interface StatusLineProps extends StatusLineData {
  statusLineYValue: number;
}

type StatusLineText = Pick<StatusLineProps, 'statusLineYValue'>;
interface StatusLineHoverPointProps {
  statusLines: StatusLineData[];
  getClosest: GetClosest;
  statusLineYValue: number;
}

interface TimelineCardProps extends ToggleCardProps {
  lines: LineData[];
  xTicks?: number[] | number;
  yTicks?: number[] | number;
  timeFormat?: TimeFormat;
  initialWidth?: number;
  aspectRatio?: number;
  hasGradient?: boolean;
  yDomain?: Graph.Domain;
  xDomain?: Graph.Domain;
  margin?: Margin;
  valueFormatter: Formatter;
  statusLines?: StatusLineData[];
  statusLineYValue?: number;
}

const Line: React.FC<LineProps> = ({
  points,
  stops = [],
  index,
  color = 'text-default',
  showScatter = false,
  getClosest,
  hideTimeline = false,
}) => {
  const theme = useTheme();

  const hasGradient = stops.length > 0;

  const resolvedColor = theme.color[color];

  const gradientId = `path-gradient-${resolvedColor}-${Math.random()}-${index}`;

  const closest = getClosest(points);

  const domain = Graph.getDomain(points);

  return (
    <>
      <Visible if={!hideTimeline}>
        <Visible if={stops}>
          <Graph.LinearGradient
            relativeDomain={domain.y}
            id={gradientId}
            direction="vertical"
            stops={stops}
          />
        </Visible>

        <Graph.PlotLine
          strokeWidth={2}
          stroke={resolvedColor}
          gradientId={hasGradient ? gradientId : undefined}
          stops={stops}
          data={points}
        />

        <Visible if={showScatter}>
          <Graph.PlotScatter data={points} fill={resolvedColor} radius={3} stops={stops} />
        </Visible>

        {closest ? (
          <>
            <Graph.Point
              x={closest.x}
              y={closest.y}
              fill={theme.color['text-default']}
              radius={5}
            />
            <Graph.PlotScatter data={[closest]} fill={resolvedColor} radius={3} stops={stops} />
            <Graph.Tooltip
              x={closest.x}
              y={closest.y}
              yOffset={8}
              xOffset={8}
              color={theme.color.base}
              text={closest.text}
              backgroundColor={theme.color['text-default']}
            />
          </>
        ) : null}
      </Visible>
    </>
  );
};

export const StatusLine: React.FC<StatusLineProps> = ({
  color,
  end,
  start,
  width = 4,
  statusLineYValue = 0,
}) => {
  const theme = useTheme();

  return (
    <>
      <Graph.YLine
        yVal={statusLineYValue}
        xStart={start}
        xEnd={end}
        strokeWidth={width}
        stroke={theme.color[color]}
      />
    </>
  );
};

export const StatusText: React.FC<StatusLineText> = ({ statusLineYValue = 0 }) => {
  const theme = useTheme();

  const { yScale } = usePlotDataContext(statusLineYValue);
  const plot = yScale(statusLineYValue);

  return (
    <>
      <Text
        fontFamily={FONT_FAMILY.regular}
        fontSize={FONT_SIZE.captionSmall}
        alignmentBaseline="central"
        fill={theme.color['text-default']}
        textAnchor="start"
        y={plot}
      >
        EWS scale
      </Text>
    </>
  );
};

export const StatusLineHoverPoint: React.FC<StatusLineHoverPointProps> = ({
  statusLines,
  getClosest,
  statusLineYValue,
}) => {
  const theme = useTheme();

  const points = statusLines.map<TimelinePointWithColor>(line => {
    return {
      y: statusLineYValue,
      x: (line.start + line.end) / 2,
      text: line.text,
      color: line.color,
    };
  });

  const closestPoint = getClosest(points);

  const closestPointWithColor = points.find(line => line.x === closestPoint?.x);

  if (!closestPointWithColor) {
    return null;
  }

  return (
    <>
      <Graph.Point
        x={closestPointWithColor.x}
        y={statusLineYValue}
        fill={theme.color['text-default']}
        radius={5}
      />
      <Graph.PlotScatter
        data={[
          {
            x: closestPointWithColor.x,
            y: statusLineYValue,
          },
        ]}
        fill={theme.color[closestPointWithColor.color]}
        radius={3}
      />
      <Graph.Tooltip
        x={closestPointWithColor.x}
        y={statusLineYValue}
        yOffset={8}
        xOffset={8}
        color={theme.color.base}
        text={closestPointWithColor.text}
        backgroundColor={theme.color['text-default']}
      />
    </>
  );
};
const getMaxMinDomain = (arr1: Graph.Domain | undefined, arr2: Graph.Domain): Graph.Domain => {
  if (!arr1) {
    return arr2;
  }
  const mergedArray = [...arr1, ...arr2];
  const max = Math.max(...mergedArray);
  const min = Math.min(...mergedArray);

  return [min, max];
};

export const TimelineCard: React.FC<TimelineCardProps> = ({
  lines,
  valueFormatter,
  initialWidth,
  timeFormat = 'time',
  aspectRatio = 1,
  xTicks,
  yTicks,
  margin,
  yDomain,
  xDomain,
  statusLineYValue = 0,
  statusLines = [],
  ...toggleCardProps
}) => {
  const theme = useTheme();
  const { layout, onLayout } = useLayoutDimensions({ initialWidth });

  const { onHover, getClosest } = useTooltipProps();
  const xFormatter = timeFormatters[timeFormat];

  const domain = Graph.getDomain(lines.map(l => l.points).flat());

  const xDomains = xDomain || domain.x;

  const firstXValue = first(xDomains);
  const lastXValue = last(statusLines)?.end;
  const endDate = Date.now();
  const statusLineDomain: Graph.Domain = [firstXValue ?? 0, lastXValue ?? endDate];

  const resolveXDomain = getMaxMinDomain(xDomains, statusLineDomain);

  const resolveDomain = resolveXDomain ? resolveXDomain : xDomains;

  return (
    <ToggleCard {...toggleCardProps}>
      <Skeleton>
        <View onLayout={onLayout}>
          <Graph.LinearPlot
            baseWidth={layout.width}
            aspectRatio={aspectRatio}
            margin={margin}
            useNiceScaleY
            onHoverDebounce={0}
            onHover={onHover}
            // use selecting as a replacement for hovering on native
            onSelectEnd={onHover}
            xDomain={resolveDomain}
            yDomain={yDomain || domain.y}
            overflow="visible"
          >
            <Graph.Grid
              color={theme.color['text-default']}
              stroke={theme.color.divider}
              yFormatter={valueFormatter}
              xFormatter={xFormatter}
              xTicks={xTicks}
              yTicks={yTicks}
              yAxis
              xAxis
              yGrid
            />

            {lines.map((props, index) => (
              <Line {...props} key={index} index={index} getClosest={getClosest} />
            ))}
            <StatusText statusLineYValue={statusLineYValue} />
            {statusLines.map(statusLine => (
              <StatusLine {...statusLine} statusLineYValue={statusLineYValue} />
            ))}

            <StatusLineHoverPoint
              statusLines={statusLines}
              getClosest={getClosest}
              statusLineYValue={statusLineYValue}
            />
          </Graph.LinearPlot>
        </View>
      </Skeleton>
    </ToggleCard>
  );
};
